<template>
  <zm-context namespace="zbo" :system-fonts="true">
    <router-view
      v-if="ready"
      class="opc-page"
      :class="{
        'opc-page-mobile': is_mobile_reactive,
        'opc-page-desktop': is_desktop_reactive,
        'opc-page-tablet': is_tablet_reactive,
      }"
    ></router-view>
    <component :is="pcm.Holder" />
  </zm-context>
  <!-- TODO v-if = init ready -->
</template>

<script>
import { ZmContext } from '@zoom/zoom-ui-vue3';
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import { debounce, omit } from 'lodash';
import { COMMON_ACTION_TYPES } from '@/store/modules/common';
import { logger } from '@/utils/log';
import { isEmptyVal } from '@/utils';
import { setupLnProfile } from '@/utils/tm';
import { pageConfig } from '@/common/service/page-config';
import { CouponErrorHandler } from '@/views/config/coupon/helper/error-handler';
import { PartnerErrorHandler } from '@/views/config/partner-refer/helper/error-handler';
import { getFlowRedirectMessage } from '@/common/service/flow/redirect-message';
import { BaseFreeTrialErrorHandler } from '@zoom/billing-coupon';
import { setCartRequest } from '@/apis/cart';
import { getPersistShoppingBagLink, persistCartConstant } from '@/views/config/shopping-cart/persist/persist-cart';
import { shoppingCartPlanInitReady } from '@/utils/ready';
import { needCustomJudgeShoppingCartPlanInit } from '@/views/config/coupon/helper/condition';
import EventTracking from '@/aspects/trackings';
import pcm from "@/global-install/global-pcm";
import promotionMixin from '@/common/mixin/promotion-mixin';
import zcmDomainCheckService from '@/common/service/zcm-domain-check';
import { formatPersistInfoFromShoppingCart } from '@/views/config/shopping-cart/persist/persist-cart';
import { getCurrency } from '@/utils/currency';

export default {
  mixins: [promotionMixin],
  data() {
    return {
      ready: false,
      pcm: Object.freeze(pcm),
    };
  },
  components: {
    ZmContext,
  },
  async mounted() {
    // TODO await call api to init raw plan map
    this.init();
    // Initialize ZCM domain check service
    zcmDomainCheckService.init(this.$store);
  },
  computed: {
    ...mapState({
      persist: (state) => state.ShoppingCart.persist,
    }),
    ...mapGetters(['cartIsReady', 'planMapFlat', 'currentEditPlan', 'getSelectedPlans', 'lnProfile', 'healthcareInfo', 'openInvoiceCartItemCount', 'partnerRefer']),
    isLnReady() {
      // LexusNexus fraud detection (originally phone plans, but opening up to all flows to debug issue with unknown profile sessions on checkout)
      return !!this.lnProfile?.domain;
    },
    cartPlanCount() {
      return (this.getSelectedPlans.length || 0) + this.openInvoiceCartItemCount;
    },
    watchCartReadyWithPlanCount() {
      return {
        planCount: this.cartPlanCount,
        cartIsReady: this.cartIsReady,
      };
    },
  },
  methods: {
    ...mapMutations(['savePersist']),
    ...mapActions({
      basicConfig: COMMON_ACTION_TYPES.basicConfig, // not have permission
      billingConfig: 'getBasicConfig', // have account permission
    }),
    getBasicConfig() {
      return this.basicConfig().finally(() => {
        this.ready = true;
      });
    },
    async init() {
      try {
        await Promise.all([this.getBasicConfig(), this.billingConfig()]);
      } catch (e) {
        logger.error('init app', e);
      }
    },
    showErrorMessage(message) {
      message &&
        this.$message({
          type: 'info',
          duration: 50000,
          message,
          showClose: true,
          iconClass: 'zm-icon-info',
          dangerouslyUseHTMLString: true,
          customClass: 'opc-global-info-message',
        });
    },
    popUpErrorMessage() {
      const { couponErrorResult, partnerReferralErrorResult, partnerReferral, freeTrialErrorResult } = pageConfig;

      // only for coupon error handler
      if (!isEmptyVal(couponErrorResult)) {
        const instance = new CouponErrorHandler(couponErrorResult, this.planMapFlat);
        const message = instance.getErrorMsgWithErrorCode();
        return this.showErrorMessage(message);
      }

      // only not get partner referral meta info will pop up error message
      if (!isEmptyVal(partnerReferralErrorResult) && isEmptyVal(partnerReferral)) {
        const instance = new PartnerErrorHandler(partnerReferralErrorResult);
        const message = instance.getErrorMsgWithErrorCode();
        return this.showErrorMessage(message);
      }

      // only for free trial instance id error cases
      if (!isEmptyVal(freeTrialErrorResult)) {
        const instance = new BaseFreeTrialErrorHandler(freeTrialErrorResult);
        const message = instance.getErrorMsgWithErrorCode();
        return this.showErrorMessage(message);
      }

      const redirectMessage = getFlowRedirectMessage({ currentEditPlan: this.currentEditPlan });

      return this.showErrorMessage(redirectMessage);
    },

    setCartInfo: debounce(function (cartInfo) {
      try {
        const { persist, ...rest } = cartInfo ?? {};
        const data = JSON.stringify({
          link: getPersistShoppingBagLink(),
          planCount: this.cartPlanCount,
          persist: formatPersistInfoFromShoppingCart(persist, { partnerRefer: this.partnerRefer }),
          currency: getCurrency(),
          ...rest,
        });
        setCartRequest(data);
      } catch (error) {
        logger.error(error);
      }
    }, 500),
  },
  watch: {
    cartIsReady: {
      handler(cartIsReady) {
        cartIsReady && this.popUpErrorMessage();
        cartIsReady &&
          window?.__hook_opc_cart_ready__?.({
            $app: this.$root,
            $store: this.$store,
            $context: this.$context,
          });
        this.savePersist();
        if (cartIsReady && !needCustomJudgeShoppingCartPlanInit()) {
          shoppingCartPlanInitReady.solved();
        }
      },
    },
    isLnReady: {
      handler(newVal, oldVal) {
        if (newVal && !oldVal) {
          setupLnProfile(this.lnProfile);
        }
      },
    },
    watchCartReadyWithPlanCount: {
      deep: true,
      handler() {
        if (this.watchCartReadyWithPlanCount.cartIsReady && persistCartConstant.enablePersistShoppingBag) {
          window.top.postMessage({
            type: 'cart-update',
            planCount: this.watchCartReadyWithPlanCount.planCount,
            link: getPersistShoppingBagLink(),
          });

          if (!this.watchCartReadyWithPlanCount.planCount) {
            EventTracking.persistentCart('cart empty state - view');
          }
        }
      },
    },

    persist: {
      deep: true,
      handler(persistInfo) {
        if (this.cartIsReady && persistCartConstant?.enablePersistShoppingBag) {
          const persistWithoutTimestamp = omit(persistInfo, ['_']);

          this.setCartInfo({
            persist: persistWithoutTimestamp,
            extension: {
              healthcareInfo: this.healthcareInfo,
            },
          });
        }
      },
    },
    healthcareInfo: {
      deep: true,
      handler(newValue) {
        if (this.cartIsReady && persistCartConstant?.enablePersistShoppingBag) {
          const persistWithoutTimestamp = omit(this.persist, ['_']);
          this.setCartInfo({
            persist: persistWithoutTimestamp,
            extension: {
              healthcareInfo: newValue,
            },
          });
        }
      },
    },
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-global-info-message {
  &.zbo-message {
    background: var(--zoom-color-bg-default, #FFFFFF);

    .zm-icon-info {
      margin-right: 16px;
      color: #0b5cff;
      font-size: 16px;
    }
  }
}

// * enhance responsive
.opc-page-desktop {
  min-width: 976px;
  // * due to out-container #content_container responsive style change, add padding-top to cover page bottom body color
  @include media(mobile, tablet) {
    padding-top: 50px;
  }
  .shopping-cart {
    @include media(mobile, tablet) {
      padding-top: 40px;
    }
  }
}
</style>
